import { useAsyncNew } from 'hooks/useAsyncNew';
import NetworkAppsService from 'services/network-apps/NetworkApps';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Chip, FormControlLabel, Stack, SvgIcon, Typography } from '@mui/material';
import ServerAutoComplete from 'components/shared/server-auto-complete';
import SimpleSelect from 'components/select/SimpleSelect';
import CloseIcon from '@mui/icons-material/Close';
import Search from 'components/shared/search';
import { useSelector } from 'react-redux';
import StoreService from 'services/store/Store';
import CustomSwitch from 'components/shared/switch/Switch';
import { ReactComponent as ByocLogo } from 'assets/icons/byoc_logo.svg';
import { isWhitelabelPortal } from 'const/PortalType';
import OnechipsService from 'services/onechips/Onechips';
import ClientsService from 'services/clients/Clients';
import { useTranslation } from 'react-i18next';

const predefinedData = {
  coverageTypes: [
    { value: 'Single', label: 'Single' },
    { value: 'Multi', label: 'Multi' },
  ],
  credentialTypes: [
    {
      value: 'global',
      label: 'Global',
    },
    {
      value: 'local',
      label: 'Local',
    },
    {
      value: 'native',
      label: 'Native',
    },
  ],
  networkTypes: [
    {
      value: 'Public',
      label: 'Public Network',
    },
    {
      value: 'Private',
      label: 'Private Network',
    },
  ],
  radioTechs: [
    { value: '5G', label: '5G' },
    { value: '3G', label: '3G' },
    { value: '2G', label: '2G' },
    { value: 'LTE', label: 'LTE' },
    { value: 'LTE_M', label: 'LTE_M' },
    { value: 'LTE_3G', label: 'LTE/3G' },
    { value: 'P_LTE', label: 'Private LTE' },
    { value: 'NB-IoT', label: 'NB-IoT' },
    { value: 'Other', label: 'Other' },
  ],
  services: [
    {
      value: 'meerkat',
      label: 'Meerkat',
    },
  ],
};

const SelectedFilters = ({ filters, readOnly, clearFilter }) => {
  const allowed = [
    'name',
    'coverageCountries',
    'networkType',
    'coverageType',
    'networkTechType',
    'service',
    'credentialsType',
    'networkProviders',
    'sku',
  ];

  const list = Object.entries(filters)
    .filter(([filterName, val]) => allowed.includes(filterName) && val)
    .reduce((acc, [filterName, filterValues]) => {
      acc[filterName] = Array.isArray(filterValues)
        ? filterValues
        : [
            {
              label: filterValues,
              value: filterName,
            },
          ];
      return acc;
    }, {});

  return (
    <>
      {Object.entries(list).map(([filterName, filterValues]) => (
        <Fragment key={filterName}>
          {filterValues.map((filter) => (
            <Chip
              color="primary"
              disabled={readOnly[filterName]}
              key={filter.value}
              label={filter.label}
              onDelete={() => clearFilter(filterName, filter.value)}
              deleteIcon={<CloseIcon />}
            />
          ))}
        </Fragment>
      ))}
    </>
  );
};

const CountriesList = ({ onSelect, selected }) => {
  const handleCountryMenuItemSelect = (values) => {
    onSelect(values);
  };

  const countries = useAsyncNew(NetworkAppsService.getCountriesList, { immediate: false });

  const handleOnCountrySearch = async (value) => {
    await countries.execute({ title: value, limit: 10, 'sort.title': 'ASC' });
  };

  return (
    <ServerAutoComplete
      label={'Countries'}
      InputProps={{
        label: 'Countries',
        placeholder: null,
      }}
      size="medium"
      multiple={true}
      getOptionValue={(option) => option.isoCode}
      onSelect={(items) => handleCountryMenuItemSelect(items)}
      onOpen={() => countries.execute()}
      loading={countries.isLoading}
      onSearch={handleOnCountrySearch}
      data={countries?.data?.countries}
      transform={(item) => item}
      getOptionLabel={(option) => option?.name || ''}
      value={selected}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.isoCode}>
            {option.name}
          </li>
        );
      }}
    />
  );
};

const SkuSelectList = ({ value, onChange, ...props }) => {
  const clientId = useSelector((state) => {
    const { id: clientId, uuid: clientUuid } = state.account.selected;
    return clientId;
  });

  const [isOpenedOnce, setOpenedOnce] = useState(false);

  const storeItems = useAsyncNew(ClientsService.getClientsStoreItems, { immediate: false });

  useEffect(() => {
    setOpenedOnce(false);
  }, [clientId]);

  useEffect(() => {
    if (!isOpenedOnce && storeItems.status === 'idle' && value.filter((val) => val).length) {
      storeItems.execute({ clientId });
    }
  }, [value]);

  const handleOpen = () => {
    if (isOpenedOnce) return;
    setOpenedOnce(true);
    storeItems.execute({ clientId });
  };

  return (
    <SimpleSelect
      multiple={true}
      loading={storeItems.isLoading}
      onMenuItemSelect={(items) => onChange('sku', items)}
      label={'SKU'}
      getOptionValue={(option) => option.uuid}
      getOptionLabel={(option) => option.sku}
      menuItems={storeItems?.data?.data?.data ?? []}
      selectedFilters={value}
      onOpen={handleOpen}
      {...props}
    />
  );
};

const Byoc = ({ onChange }) => {
  return (
    <Stack
      direction={'row'}
      height={'40px'}
      width={'193px'}
      spacing={2}
      px={1.5}
      sx={{ borderRadius: '4px', backgroundColor: '#F4F5F5' }}
      justifyContent={'space-between'}
      alignItems={'center'}
      alignContent={'center'}
    >
      <FormControlLabel
        sx={{
          '&.MuiFormControlLabel-root': {
            width: '100%',
            justifyContent: 'space-between',
          },
        }}
        onChange={onChange}
        disableTypography
        control={<CustomSwitch />}
        label={
          <Stack direction={'row'} spacing={1} alignItems={'self-end'}>
            <SvgIcon component={ByocLogo} sx={{ width: 69, height: 20 }} inheritViewBox />
            <Typography>only</Typography>
          </Stack>
        }
      />
    </Stack>
  );
};

const PlanFilters = ({ isModal = false, filters = {}, selected = {}, readOnly = {}, onChange = () => {} }) => {
  const { t } = useTranslation();

  const selectedList = selected;

  const [operators, setOperators] = useState({});

  const handleOnSetFilter = (filterName, values) => {
    onChange(filterName, values);
  };

  /**
   * Clear individual filter
   */
  const clearFilter = (name, value) => {
    if (name === 'name') {
      handleOnSetFilter(name, '');
      return;
    }

    if (name === 'coverageCountries') {
      setOperators((prev) => {
        const { [value]: _, ...rest } = prev;
        return rest;
      });
    }

    const updatedValues = filters[name]?.filter((filter) => filter.value !== value);

    handleOnSetFilter(name, updatedValues);
  };

  /**
   * Generic select handler
   */
  const handleMenuItemSelect = (filterName, values) => {
    handleOnSetFilter(filterName, values);
  };

  /**
   * Countries → Operators dependency
   */
  const handleCountryMenuItemSelect = (values) => {
    const operatorsMap = values.reduce((acc, item) => {
      acc[item.value] = item.raw.networkProviders;
      return acc;
    }, {});

    setOperators(operatorsMap);
    handleOnSetFilter('coverageCountries', values);
  };

  const handleOnSearch = (value) => {
    handleOnSetFilter('name', value);
  };

  const handleOnByocCheck = (value) => {
    handleOnSetFilter('byoc', value);
  };

  const getOperators = () => {
    return Object.values(operators).flat();
  };

  return (
    <Stack spacing={2} alignItems="start" sx={{ width: '100%', mb: 4 }}>
      <Stack spacing={2} direction="row" alignItems="center">
        <Search
          placeholder={t('views.network_apps.filters.search.placeholder')}
          value={selectedList?.name ?? ''}
          onSearch={handleOnSearch}
          sx={{ width: '100%' }}
        />
        {!isWhitelabelPortal(CONFIG.app.portalType) && <Byoc onChange={(e) => handleOnByocCheck(e.target.checked)} />}
      </Stack>

      <Stack spacing={1} direction={isModal ? 'column' : 'row'} sx={{ width: '100%', minHeight: '40px' }}>
        <Stack direction="row" spacing={1} flex={1}>
          <Box flex={1} minWidth="133px">
            <CountriesList selected={selectedList?.coverageCountries ?? []} onSelect={handleCountryMenuItemSelect} />
          </Box>

          <Box flex={1} minWidth="133px">
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.networkProviders ?? false }}
              disabled={!Object.keys(operators).length}
              multiple
              readOnly={readOnly?.networkProviders ?? false}
              onMenuItemSelect={(items) => handleMenuItemSelect('networkProviders', items)}
              label="Operators"
              menuItems={getOperators()}
              selectedFilters={selectedList?.networkProviders ?? []}
            />
          </Box>

          <Box flex={1} minWidth="133px">
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.networkTechType ?? false }}
              multiple
              onMenuItemSelect={(items) => handleMenuItemSelect('networkTechType', items)}
              label="Radio Tech"
              getOptionValue={(item) => item.value}
              getOptionLabel={(item) => item.label}
              menuItems={predefinedData.radioTechs}
              selectedFilters={selectedList?.networkTechType ?? []}
            />
          </Box>

          <Box flex={1} minWidth="133px">
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.coverageType ?? false }}
              multiple
              onMenuItemSelect={(items) => handleMenuItemSelect('coverageType', items)}
              label="Coverage"
              getOptionValue={(item) => item.value}
              getOptionLabel={(item) => item.label}
              menuItems={predefinedData.coverageTypes}
              selectedFilters={selectedList?.coverageType ?? []}
            />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flex={1}>
          <Box flex={1} minWidth="133px">
            <SimpleSelect
              multiple
              inputProps={{ readOnly: readOnly?.service ?? false }}
              onMenuItemSelect={(items) => handleMenuItemSelect('service', items)}
              label="Services"
              getOptionValue={(item) => item.value}
              getOptionLabel={(item) => item.label}
              menuItems={predefinedData.services}
              selectedFilters={selectedList?.service ?? []}
            />
          </Box>

          <Box flex={1} minWidth="133px">
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.credentialsType ?? false }}
              multiple
              onMenuItemSelect={(items) => handleMenuItemSelect('credentialsType', items)}
              label="Credential"
              getOptionValue={(item) => item.value}
              getOptionLabel={(item) => item.label}
              menuItems={predefinedData.credentialTypes}
              selectedFilters={selectedList?.credentialsType ?? []}
            />
          </Box>

          <Box flex={1} minWidth="133px">
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.networkType ?? false }}
              multiple
              onMenuItemSelect={(items) => handleMenuItemSelect('networkType', items)}
              label="Networks"
              getOptionValue={(item) => item.value}
              getOptionLabel={(item) => item.label}
              menuItems={predefinedData.networkTypes}
              selectedFilters={selectedList?.networkType ?? []}
            />
          </Box>

          <Box flex={1} minWidth="133px">
            <SkuSelectList
              onChange={handleMenuItemSelect}
              value={selectedList?.sku ?? []}
              inputProps={{ readOnly: readOnly?.sku ?? false }}
            />
          </Box>
        </Stack>
      </Stack>

      <Stack spacing={1} direction="row">
        <SelectedFilters readOnly={readOnly} clearFilter={clearFilter} filters={filters} />
      </Stack>
    </Stack>
  );
};

export default PlanFilters;
